import { CustomerReservesComponent } from './customer-reserves/customer-reserves.component';
import { OrderFormComponent } from '../order-management/order-form/order-form.component';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { RmsModule } from '@rms';
import { Column, HeaderButton } from '@interfaces';
import { AuthService, CustomerCrmService, RmsMessageService, RmsPulldownService } from '@services';
import { CustomerContactsComponent } from './customer-contacts/customer-contacts.component';
import { CustomerAddressesComponent } from './customer-addresses/customer-addresses.component';
import { AddressFormComponent } from './customer-addresses/address-form/address-form.component';
import { CustomerPaymentPreferencesComponent } from './customer-payment-preferences/customer-payment-preferences.component';
import { CustomerNotesComponent } from './customer-notes/customer-notes.component';
import { CustomerSamplesComponent } from './customer-samples/customer-samples.component';
import { CustomerOrdersComponent } from './customer-orders/customer-orders.component';
import { CustomerPaymentsTabComponent } from './customer-payments-tab/customer-payments-tab.component';
import { CustomerAuditComponent } from './customer-audit/customer-audit.component';
import { NoteFormComponent } from './customer-notes/note-form/note-form.component';
import { SampleRequestFormComponent } from './customer-samples/sample-request-form/sample-request-form.component';
import { NewCustomerFormComponent } from './new-customer-form/new-customer-form.component';
import { CustomerOverviewFormComponent } from './customer-overview-form/customer-overview-form.component';

@Component({
  selector: 'app-customer-management',
  imports: [RmsModule, CustomerReservesComponent, CustomerContactsComponent, CustomerAddressesComponent, CustomerPaymentPreferencesComponent, CustomerNotesComponent, CustomerSamplesComponent, CustomerOrdersComponent, CustomerPaymentsTabComponent, CustomerAuditComponent],
  templateUrl: './customer-management.component.html',
  styleUrl: './customer-management.component.scss'
})
export class CustomerManagementComponent implements OnInit, OnDestroy {
  SearchResults: any[] = [];
  SearchTerm = '';
  IncludeInactive = false;
  Loading = false;
  DetailsLoading = false;
  OverviewARBalance: number | null = null;
  OverviewPastDue: number | null = null;
  private searchRequest = 0;
  private detailsRequest = 0;
  private searchTimer: ReturnType<typeof setTimeout> | undefined;
  SelectedCustomer: any = null;
  CustomerDetails: any = null;
  RelatedCustomers: any[] = [];
  CustomerActive: boolean = true;
  OverviewPaymentMethodName: string = '—';
  OverviewPaymentTermName: string = '—';
  OverviewPaymentTermId: number | null = null;

  // Active Customers and Sample Follow-ups now come from get_customer_dashboard_stats.
  StatActiveCustomers: string = '—';
  StatPastDue: string = '—';
  StatSampleFollowUps: string = '—';

  SearchColumns: Column[] = [
    { label: 'ID', key: 'CompanyID', width: 8 },
    { label: 'Customer', key: 'CompanyName', width: 22 },
    { label: 'Owner Group', key: 'OwnerGroupName', width: 15 },
    { label: 'Coffee Trader', key: 'CoffeeTrader', width: 14 },

    { label: 'Email', key: 'EmailAddress', width: 20 },
    { label: 'Phone', key: 'TelephoneNumber', width: 12 },
    { label: 'City', key: 'City', width: 12 },
    { label: 'State', key: 'State', width: 6 },
    { label: 'Payment', key: 'Payment', width: 10 },
    { label: 'Status', key: 'Status', width: 10, SearchType: 'select', cellClass: (row: any) => row.Inactive ? 'status-text-inactive' : 'status-text-active' },
    { label: 'Last Order', key: 'LastOrder', width: 12 },
  ];

  Buttons: HeaderButton[] = [
    { label: '+ New Customer', class: 'btn-bg-green', onAction: () => this.newCustomer() }
  ];

  // view is unified for everyone; editing across every business-area tab
  // (Contacts, Addresses, Payment & Terms, Notes, Samples) is restricted to
  // Trader/Admin/Executive can edit
  static readonly EDITOR_GROUPS = ['Trader', 'Senior Trader', 'Senior Traders', 'Traders', 'System Admins', 'admins', 'Executive'];
  CanEdit = false;

  constructor(private crm: CustomerCrmService, private message: RmsMessageService, private pulldown: RmsPulldownService, private auth: AuthService) { }

  ngOnInit() {
    this.CanEdit = this.auth.isInAnyGroup(CustomerManagementComponent.EDITOR_GROUPS);
    this.Buttons[0].disabled = !this.CanEdit;
    this.loadCustomers();
    this.loadStats();
  }

  loadStats() {
    this.crm.getDashboardStats().subscribe({
      next: (stats) => {
        this.StatActiveCustomers = stats?.ActiveCustomerCount != null ? String(stats.ActiveCustomerCount) : '—';
        this.StatSampleFollowUps = stats?.SampleFollowUpCount != null ? String(stats.SampleFollowUpCount) : '—';
      },
      error: (err) => this.message.error('Failed to load dashboard stats: ' + this.errorText(err))
    });

    this.crm.getPastDueSummary().subscribe({
      next: (stats) => {
        this.StatPastDue = stats?.TotalPastDueAmount != null
          ? '$' + Number(stats.TotalPastDueAmount).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
          : '—';
      },
      error: (err) => this.message.error('Failed to load past due summary: ' + this.errorText(err))
    });
  }

  loadCustomers() {
    const request = ++this.searchRequest;
    this.Loading = true;
    this.crm.searchCustomers(this.SearchTerm.trim(), this.IncludeInactive).subscribe({
      next: (results) => {
        if (request !== this.searchRequest) return;
        this.Loading = false;
        this.SearchResults = (results || []).map((r: any) => ({
          ...r,
          Status: r.Inactive ? 'Inactive' : 'Active',
          CoffeeTrader: r.TraderName || '—',
          Payment: r.PaymentMethodName || '—',
          LastOrder: r.LastOrderDate ? new Date(r.LastOrderDate).toLocaleDateString() : '—'
        }));
      },
      error: (err) => { if (request !== this.searchRequest) return; this.Loading = false; this.message.error('Failed to search customers: ' + this.errorText(err)); }
    });
  }

  searchChanged() {
    clearTimeout(this.searchTimer);
    ++this.searchRequest;
    this.searchTimer = setTimeout(() => this.loadCustomers(), 300);
  }

  ngOnDestroy() { clearTimeout(this.searchTimer); ++this.searchRequest; ++this.detailsRequest; }

  newCustomer() {
    if (!this.CanEdit) return;
    const ref = this.pulldown.open(NewCustomerFormComponent, {
      title: 'New Customer',
      width: '70vw',
      maxWidth: '70vw',
      maxHeight: '90vh',
      center: true,
      panelClass: 'new-customer-centered-pane'
    });

    ref.afterClosed().subscribe((result: any) => {
      if (!result || result.cancelled) return;
      result.ModifiedBy = localStorage.getItem('GUID');
      this.crm.createCustomer(result).subscribe({
        next: (created) => {
          this.message.success('Customer created successfully');
          this.loadCustomers();
          this.loadStats();
          if (created?.CompanyID) {
            this.selectCustomer({ row: { CompanyID: created.CompanyID, CompanyName: result.CompanyName } });
          }
        },
        error: (err) => this.message.error('Failed to create customer: ' + this.errorText(err))
      });
    });
  }



  selectCustomer(event: { row: any }) {
    this.SelectedCustomer = event.row;
    this.CustomerDetails = null;
    this.OverviewPaymentMethodName = '—';
    this.OverviewPaymentTermName = '—';
    this.OverviewPaymentTermId = null;
    this.loadDetails();
  }

  loadDetails() {
    if (!this.SelectedCustomer) return;
    const companyId = this.SelectedCustomer.CompanyID;
    const request = ++this.detailsRequest;
    this.DetailsLoading = true;
    this.RelatedCustomers = [];
    this.crm.getRelatedCustomers(companyId).subscribe({
      next: (customers) => { if (request === this.detailsRequest) this.RelatedCustomers = customers || []; },
      error: (err) => { if (request === this.detailsRequest) this.message.error('Related customers could not be loaded: ' + this.errorText(err)); }
    });
    this.crm.getCustomerDetails(companyId).subscribe({
      next: (details) => {
        if (request !== this.detailsRequest || this.SelectedCustomer?.CompanyID !== companyId) return;
        this.DetailsLoading = false;
        this.CustomerDetails = details;
        this.CustomerActive = !details?.Inactive;
      },
      error: (err) => { if (request !== this.detailsRequest) return; this.DetailsLoading = false; this.message.error('Failed to load customer details: ' + this.errorText(err)); }
    });
    this.crm.getPaymentPreference(companyId).subscribe({
      next: (pref) => {
        if (request !== this.detailsRequest || this.SelectedCustomer?.CompanyID !== companyId) return;
        this.OverviewARBalance = pref?.ARBalance ?? null;
        this.OverviewPastDue = pref?.PastDueAmount ?? null;
        this.OverviewPaymentMethodName = pref?.MethodName || '—';
        this.OverviewPaymentTermName = pref?.PaymentTermName || '—';
        this.OverviewPaymentTermId = pref?.PaymentTermId ?? null;
      },
      error: () => {
        if (request !== this.detailsRequest) return;
        this.OverviewPaymentMethodName = '—';
        this.OverviewPaymentTermName = '—';
        this.OverviewPaymentTermId = null;
      }
    });
  }

  editDefaultBillingAddress() {
    if (!this.CanEdit) return;
    const ref = this.pulldown.open(AddressFormComponent, {
      title: 'Edit Billing Address',
      width: 'clamp(480px, 50vw, 720px)',
      data: {
        CompanyID:this.SelectedCustomer.CompanyID,
        CustomerShipToAddressID: this.CustomerDetails.DefaultBillingAddressID,
        AddressName: this.CustomerDetails.DefaultBillingAddressName,
        Street: this.CustomerDetails.DefaultBillingStreet,
        City: this.CustomerDetails.DefaultBillingCity,
        State: this.CustomerDetails.DefaultBillingState,
        ZipCode: this.CustomerDetails.DefaultBillingZip,
        Country: this.CustomerDetails.DefaultBillingCountry,
        AddressType: 'Billing',
        IsActive: true,
        IsDefault: true
      }
    });

    ref.afterClosed().subscribe((result: any) => {
      if (!result || result.cancelled) return;
      result.CompanyID = this.SelectedCustomer.CompanyID;
      result.AddressType = 'Billing';
      result.ModifiedBy = localStorage.getItem('GUID');
      this.crm.saveShipToAddress(result).subscribe({
        next: () => {
          this.message.success('Billing address updated successfully');
          this.loadDetails();
        },
        error: (err) => this.message.error('Failed to save billing address: ' + this.errorText(err))
      });
    });
  }

  createOrder() {
    if (!this.CanEdit || !this.SelectedCustomer || this.CustomerDetails?.Inactive) return;
    this.pulldown.open(OrderFormComponent, { title: 'Create Order', data: { CompanyID: this.SelectedCustomer.CompanyID }, width: '85vw', maxWidth: '95vw', maxHeight: '92vh', minHeight: '70vh', top: '0' });
  }

  editOverview() {
    if (!this.CanEdit) return;
    const ref = this.pulldown.open(CustomerOverviewFormComponent, {
      title: 'Edit Customer Overview',
      width: 'clamp(420px, 45vw, 640px)',
      data: {
        CompanyName: this.CustomerDetails.CompanyName,
        CreditLimit: this.CustomerDetails.CreditLimit,
        OciaMemberNumber: this.CustomerDetails.OciaMemberNumber,
        NonDomestic: this.CustomerDetails.NonDomestic,
        PaymentTermId: this.OverviewPaymentTermId
      }
    });

    ref.afterClosed().subscribe((result: any) => {
      if (!result || result.cancelled) return;
      result.CompanyID = this.SelectedCustomer.CompanyID;
      result.ModifiedBy = localStorage.getItem('GUID');
      this.crm.updateCustomerOverview(result).subscribe({
        next: () => {
          this.message.success('Customer overview updated successfully');
          this.loadDetails();
        },
        error: (err) => this.message.error('Failed to update customer overview: ' + this.errorText(err))
      });
    });
  }

  toggleCustomerStatus(isActive: boolean) {
    if (!this.CanEdit || !this.SelectedCustomer) return;
    const inactive = !isActive;
    this.crm.updateCustomerStatus(this.SelectedCustomer.CompanyID, inactive, localStorage.getItem('GUID')).subscribe({
      next: () => {
        this.CustomerDetails.Inactive = inactive;
        this.SelectedCustomer.Inactive = inactive;
        this.loadCustomers();
        this.loadStats();
        this.message.success(isActive ? 'Customer marked Active' : 'Customer marked Inactive');
      },
      error: (err) => {
        this.CustomerActive = !isActive;
        this.message.error('Failed to update customer status: ' + this.errorText(err));
      }
    });
  }

  clearSelection() {
    ++this.detailsRequest;
    this.SelectedCustomer = null;
    this.CustomerDetails = null;
  }

  quickAddNote() {
    if (!this.CanEdit || !this.SelectedCustomer) return;
    const ref = this.pulldown.open(NoteFormComponent, {
      title: 'Add Note',
      width: 'clamp(480px, 50vw, 720px)',
      data: {}
    });
    ref.afterClosed().subscribe((result: any) => {
      if (!result || result.cancelled) return;
      result.CompanyID = this.SelectedCustomer.CompanyID;
      result.EnteredBy = localStorage.getItem('GUID');
      this.crm.saveNote(result).subscribe({
        next: () => this.message.success('Note added — see the Notes tab for the full list'),
        error: (err) => this.message.error('Failed to save note: ' + this.errorText(err))
      });
    });
  }

  quickRequestSample() {
    if (!this.CanEdit || !this.SelectedCustomer) return;
    const ref = this.pulldown.open(SampleRequestFormComponent, {
      title: 'Request Sample',
      width: 'clamp(480px, 50vw, 720px)',
      data: {}
    });
    ref.afterClosed().subscribe((result: any) => {
      if (!result || result.cancelled) return;
      result.CompanyID = this.SelectedCustomer.CompanyID;
      result.RequestedBy = localStorage.getItem('GUID');
      this.crm.saveSampleRequest(result).subscribe({
        next: () => this.message.success('Sample requested — see the Samples tab for the full list'),
        error: (err) => this.message.error('Failed to request sample: ' + this.errorText(err))
      });
    });
  }

  private errorText(err: any): string {
    return err?.error?.error?.message || err?.message || 'Unknown error';
  }
}
